import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { FeatureFlags } from '@/types';
import type { ClientConfig, ClientMapping } from '@/types/client';
import { defaultFeatures } from '@/types/client';

// Client configurations
const clientConfigs: Record<string, ClientConfig> = {
  'classify': {
    id: 'classify',
    name: 'Classify',
    defaultOrigin: 'CN',
    defaultDestination: 'CA',
    features: {
      showExportCountry: false,
      showRules: true,
      showTariffSchedule: true,
      showTariffEligibility: true,
      showImportExportControls: true,
      showDocumentaryReqs: true,
      showGenerateCertificates: true,
      showHarmonizedSchedule: false,
      showExportSchedule: false,
      showNotes: true,
      showCommentary: false,
    },
  },
  'tariff': {
    id: 'tariff',
    name: 'Tariff',
    features: {
      showTariffSchedule: true,
      showHarmonizedSchedule: true,
    },
  },
  'carm': {
    id: 'carm',
    name: 'CARM',
    features: {},
  },
  'ghy': {
    id: 'ghy',
    name: 'GHY',
    defaultOrigin: 'CN',
    defaultDestination: 'CA',
    // Effective feature set of the legacy GHY proxy page (legacy defaults plus its
    // ghyProxy controller overrides), confirmed against the production bundle.
    features: {
      showUserDetails: false,
      enableKeywordSearch: false,
      showExportCountry: false,
      showExportSchedule: false,
      showCommentary: false,
      showRules: false,
      showHarmonizedSchedule: true,
      showTariffSchedule: true,
      showNotes: true,
      showTariffEligibility: true,
      showImportExportControls: true,
      showDocumentaryReqs: true,
      showGenerateCertificates: true,
      useTariffScheduleClick: true,
    },
  },
  'gpm': {
    id: 'gpm',
    name: 'GPM',
    features: {},
  },
  'idb': {
    id: 'idb',
    name: 'IDB',
    features: {},
  },
  'namibia': {
    id: 'namibia',
    name: 'Namibia',
    features: {},
  },
  'barbados': {
    id: 'barbados',
    name: 'Barbados',
    features: {},
  },
  'bahamas': {
    id: 'bahamas',
    name: 'Bahamas',
    defaultOrigin: 'US',
    defaultDestination: 'BS',
    // Bahamas tariff data only covers these two origins, per the customs tariff
    // search PRD, so the Origin picker must never expose the full ISO set.
    allowedOrigins: ['US', 'UM'],
    features: {
      showUserDetails: false,
      showRules: false,
      showLanguageOptions: false,
      showExportCountry: false,
      showHarmonizedSchedule: true,
      showTariffSchedule: true,
      showExportSchedule: false,
      showNotes: true,
      showCommentary: false,
      showTariffEligibility: true,
      showImportExportControls: true,
      showDocumentaryReqs: true,
      showGenerateCertificates: true,
      showCaCodes: false,
      knownInteractionsDefaultExpandedPosition: true,
    },
  },
  'eurostat': {
    id: 'eurostat',
    name: 'Eurostat',
    features: {},
  },
  'wca': {
    id: 'wca',
    name: 'WCA',
    features: {},
  },
  'uscensus': {
    id: 'uscensus',
    name: 'US Census',
    features: {},
  },
  'tjxeproxy': {
    id: 'tjxeproxy',
    name: 'TJX Proxy',
    defaultOrigin: 'CN',
    defaultDestination: 'GB',
    features: {
      showExportCountry: false,
      showCommentary: false,
      showRules: false,
      showHarmonizedSchedule: false,
      showTariffSchedule: true,
      useHSCodeBigClick: false,
      showTariffEligibility: true,
      showImportExportControls: true,
      showDocumentaryReqs: true,
      showGenerateCertificates: true,
    },
  },
  'thomsonreuters-checkpoint': {
    id: 'thomsonreuters-checkpoint',
    name: 'Thomson Reuters Checkpoint',
    features: {},
  },
  'thomsonreuters-onesource': {
    id: 'thomsonreuters-onesource',
    name: 'Thomson Reuters OneSource',
    features: {},
  },
};

// Hostname to client mapping (exact matches)
const hostnameMapping: ClientMapping = {
  'classify.3ceonline.com': 'classify',
  'classify.dev.3ceonline.com': 'classify',
  'tariff.3ceonline.com': 'tariff',
  'tariff.dev.3ceonline.com': 'tariff',
  'carm.3ceonline.com': 'carm',
  'carm.dev.3ceonline.com': 'carm',
  'ghy.3ceonline.com': 'ghy',
  'ghy.dev.3ceonline.com': 'ghy',
  'ghy.prod.3ceonline.com': 'ghy',
  'ghy.sbx.3ceonline.com': 'ghy',
  'ghy.3ce.sbx.avalara.com': 'ghy',
  'gpm.3ceonline.com': 'gpm',
  'gpm.dev.3ceonline.com': 'gpm',
  'idb.3ceonline.com': 'idb',
  'idb.dev.3ceonline.com': 'idb',
  'namibia.3ceonline.com': 'namibia',
  'namibia.dev.3ceonline.com': 'namibia',
  'barbados.3ceonline.com': 'barbados',
  'barbados.dev.3ceonline.com': 'barbados',
  'eurostat.3ceonline.com': 'eurostat',
  'eurostat.dev.3ceonline.com': 'eurostat',
  'wca.3ceonline.com': 'wca',
  'wca.dev.3ceonline.com': 'wca',
  'tjxeproxy.3ceonline.com': 'tjxeproxy',
  'tjxeproxy.dev.3ceonline.com': 'tjxeproxy',
  'tr.3ceonline.com': 'thomsonreuters-checkpoint',
  'tr.dev.3ceonline.com': 'thomsonreuters-checkpoint',
  'bahamas.3ceonline.com': 'bahamas',
  'bahamas.dev.3ceonline.com': 'bahamas',
  'localhost': 'uscensus',
};

// Read CLIENT_ROUTE from server injection (production) or Vite env (development)
function getServerClientRoute(): string | undefined {
  return window.__SERVER_CONFIG__?.clientRoute
    || import.meta.env.VITE_CLIENT_ROUTE
    || undefined;
}

// Helper to resolve hostname to client ID
function resolveHostnameToClient(hostname: string): string {
  // Prefer server-injected CLIENT_ROUTE over hostname detection
  const serverRoute = getServerClientRoute();
  if (serverRoute && clientConfigs[serverRoute]) {
    return serverRoute;
  }

  // Check exact match first
  if (hostnameMapping[hostname]) {
    return hostnameMapping[hostname];
  }
  
  // Prefix-based matches for uscensus
  if (hostname.startsWith('uscensus.') || hostname.startsWith('uscensus-')) {
    return 'uscensus';
  }
  
  return 'uscensus';
}

export const useClientStore = defineStore('client', () => {
  const clientId = ref('classify');
  const clientConfig = ref<ClientConfig>(clientConfigs['classify']);

  // Computed
  const features = computed<FeatureFlags>(() => {
    return {
      ...defaultFeatures,
      ...clientConfig.value.features,
    };
  });

  const clientName = computed(() => clientConfig.value.name);

  // Actions
  function initializeClient(): void {
    const hostname = window.location.hostname;
    const mappedClientId = resolveHostnameToClient(hostname);
    setClient(mappedClientId);
  }

  function setClient(id: string): void {
    if (clientConfigs[id]) {
      clientId.value = id;
      clientConfig.value = clientConfigs[id];
    } else {
      console.warn(`Unknown client: ${id}, falling back to classify`);
      clientId.value = 'classify';
      clientConfig.value = clientConfigs['classify'];
    }
  }

  function updateFeature<K extends keyof FeatureFlags>(
    key: K,
    value: FeatureFlags[K]
  ): void {
    clientConfig.value.features[key] = value;
  }

  function getFeature<K extends keyof FeatureFlags>(key: K): FeatureFlags[K] {
    return features.value[key];
  }

  return {
    // State
    clientId,
    clientConfig,
    // Computed
    features,
    clientName,
    // Actions
    initializeClient,
    setClient,
    updateFeature,
    getFeature,
  };
});

